import React, { useCallback, useEffect, useMemo } from 'react'
import { Handle, NodeProps, Position, useEdges, useReactFlow } from '@xyflow/react'
import { Play } from 'lucide-react'

import { IActionRepeaterValue, IActionType } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { CanvasNodeSquare } from '@makyoapp/makyo-ui'

import { isActionHasSettings, isActionPlugin } from '@/context/ActionFormProviderV2'
import { ActionDataType } from '@/shared/action.types'

import { useGenerateActionDescription } from '../utils'
import { ActionDescription } from './ActionDescription'
import { NODE_STYLES, SPECIAL_NODES } from './constants'
import { PATH_COLORS } from './constants/action-color'
import { useWorkflowContext } from './context/WorkflowContext'
import useInvalidActionOutputReference from './hooks/useInvalidActionOutputReference'
import { WorkflowManager, WorkflowNodeManager } from './managers'
import { WorkflowEdge, WorkflowNode } from './types'

interface CustomHandleProps {
  id: string
  nodeId: string
  branch?: string
  position?: Position
  style?: React.CSSProperties
  edges: any[]
  onPlusClick: (e: React.MouseEvent, branch?: string) => void
  onConnectionClick: (e: React.MouseEvent) => void
  title?: string
  dotColor?: string
  branchLabel?: string
}

const CustomHandle: React.FC<CustomHandleProps> = ({
  id,
  nodeId,
  branch,
  position = Position.Right,
  style = {},
  edges,
  onPlusClick,
  onConnectionClick,
  title = 'Add new node',
  dotColor = '',
  branchLabel,
}) => {
  const getOutgoingConnectionsForBranch = (branch?: string) => {
    return edges.filter((edge) => {
      if (edge.source !== nodeId) return false
      if (branch === undefined) return !edge.sourceHandle || edge.sourceHandle === `${nodeId}-next-action`
      return edge.sourceHandle === WorkflowNodeManager.getSourceHandle(nodeId, branch)
    })
  }

  const hasOutgoingConnections = getOutgoingConnectionsForBranch(branch).length > 0

  // Combine the dotColor into the style object if provided
  const handleStyle = dotColor ? { ...style, backgroundColor: `var(--color-${dotColor})` } : style
  return (
    <div className='relative'>
      {branchLabel && hasOutgoingConnections && (
        <div className='absolute top-1/2 -translate-y-1/2 left-4 font-light bg-white dark:bg-slate-900 p-1 text-xs z-[10] whitespace-nowrap text-gray-500 dark:text-slate-400'>
          {branchLabel}
        </div>
      )}
      <Handle
        id={id}
        type='source'
        position={position}
        style={handleStyle}
        onClick={onConnectionClick}
        className={`min-w-4! min-h-4! rounded-full! cursor-pointer border-none! z-10 hover:border-teal-500! hover:border-2! ${
          dotColor
            ? `bg-${dotColor} dark:bg-${dotColor}! hover:bg-${dotColor}! dark:hover:bg-${dotColor}!`
            : 'bg-gray-400! dark:bg-slate-500! dark:hover:bg-slate-400 hover:bg-teal-500!'
        }`}
      />
      {!hasOutgoingConnections && (
        <div
          className='absolute left-full top-1/2 -translate-y-1/2 flex items-center group/final'
          style={{
            position: 'absolute',
            left: 0,
          }}
          onClick={(e) => onPlusClick(e, branch)}
        >
          {branchLabel && (
            <div className='absolute top-1/2 -translate-y-1/2 left-4 font-light bg-white dark:bg-slate-900 p-1 text-xs z-[10] whitespace-nowrap text-gray-500 dark:text-slate-400'>
              {branchLabel}
            </div>
          )}
          <div className='w-[100px] h-0.5 bg-gray-400 dark:bg-slate-500 group-hover/final:bg-teal-500 dark:group-hover/final:bg-teal-400 transition-colors duration-200'></div>
          <button
            onClick={(e) => onPlusClick(e, branch)}
            className='w-6 h-6 bg-gray-400 dark:bg-slate-500 rounded-full flex items-center justify-center group-hover/final:bg-teal-500 dark:group-hover/final:bg-teal-400 transition-colors duration-200 hover:cursor-pointer z-[101]'
            title={title}
          >
            <MaterialIcon icon='add' size={16} className='text-white' />
          </button>
        </div>
      )}
    </div>
  )
}

export const WorkflowNodeComponent = ({ data, id }: NodeProps<WorkflowNode>) => {
  const nodeData = data
  const isTrigger = nodeData.actionValue.id === SPECIAL_NODES.trigger
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow<WorkflowNode, WorkflowEdge>()
  const { customPluginActions } = useWorkflowContext()

  const { generateActionDescription } = useGenerateActionDescription()

  const actionDescription = useMemo(() => {
    return generateActionDescription(nodeData.actionValue, customPluginActions || [])
  }, [generateActionDescription, nodeData.actionValue, customPluginActions])

  // Use useEdges hook instead of getEdges() to ensure re-renders when edges change
  const edges = useEdges()

  const invalidOutput = useInvalidActionOutputReference(nodeData.actionValue.settings)

  const handlePlusClick = useCallback(
    (e: React.MouseEvent, branch?: string) => {
      e.stopPropagation()
      const event = new CustomEvent('openNodePalette', {
        detail: {
          sourceNodeId: id,
          branch: branch, // Add branch information for conditional actions
        },
      })
      window.dispatchEvent(event)
    },
    [id]
  )

  const handleEditClick = useCallback(() => {
    // Dispatch a custom event to open the edit form
    const event = new CustomEvent('openNodeEdit', {
      detail: {
        nodeId: id,
        actionValue: nodeData.actionValue,
      },
    })
    window.dispatchEvent(event)
  }, [id, nodeData.actionValue])

  const handleConnectionClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const handleDeleteNode = useCallback(() => {
    // Don't allow deleting the trigger node
    if (isTrigger) {
      return
    }

    // Use WorkflowManager to remove the node and its edges
    const nodes = getNodes()
    const allEdges = getEdges()
    const operation = WorkflowManager.removeNode(nodes, allEdges, id)

    setNodes(operation.nodes)
    setEdges(operation.edges)

    // Trigger auto-save
    const event = new CustomEvent('triggerAutoSave')
    window.dispatchEvent(event)
  }, [id, isTrigger, getNodes, getEdges, setNodes, setEdges])

  const handleRename = useCallback(
    (newName: string) => {
      setNodes(WorkflowNodeManager.renameLabel(getNodes(), id, newName))

      const event = new CustomEvent('triggerAutoSave')
      window.dispatchEvent(event)
    },
    [id, getNodes, setNodes]
  )

  // Check if this action has settings and can be edited
  const canEdit =
    (!isTrigger && nodeData.actionValue.type && isActionHasSettings(nodeData.actionValue.type)) ||
    isActionPlugin(nodeData.actionValue)

  const defaultCustomHandleStyle: React.CSSProperties = {
    position: 'relative',
    top: 'unset',
    left: 'unset',
    right: 'unset',
    bottom: 'unset',
    transform: 'unset',
  }

  const getNextActions = () => {
    switch (nodeData.actionValue.type) {
      case IActionType.ConditionalIf:
        return [
          <CustomHandle
            key={WorkflowNodeManager.getSourceHandle(id, 'true')}
            id={WorkflowNodeManager.getSourceHandle(id, 'true')}
            nodeId={id}
            branch='true'
            style={defaultCustomHandleStyle}
            edges={edges}
            onPlusClick={handlePlusClick}
            onConnectionClick={handleConnectionClick}
            title='Add new node (True branch)'
            dotColor='green-500'
            branchLabel='True'
          />,
          <CustomHandle
            key={WorkflowNodeManager.getSourceHandle(id, 'false')}
            id={WorkflowNodeManager.getSourceHandle(id, 'false')}
            nodeId={id}
            branch='false'
            style={defaultCustomHandleStyle}
            edges={edges}
            onPlusClick={handlePlusClick}
            onConnectionClick={handleConnectionClick}
            title='Add new node (False branch)'
            dotColor='red-500'
            branchLabel='False'
          />,
        ]

      case IActionType.ConditionalSwitch:
        return nodeData.actionValue.settings?.cases?.map(
          (action: IActionRepeaterValue & { name?: string }, index: number) => (
            <CustomHandle
              key={WorkflowNodeManager.getSourceHandle(id, action.id)}
              id={WorkflowNodeManager.getSourceHandle(id, action.id)}
              nodeId={id}
              branch={action.id}
              dotColor={PATH_COLORS[index % PATH_COLORS.length].dot.replace('bg-', '')}
              edges={edges}
              onPlusClick={handlePlusClick}
              onConnectionClick={handleConnectionClick}
              title={`Add new node (Case ${index + 1})`}
              style={defaultCustomHandleStyle}
              branchLabel={action?.name ?? undefined}
            />
          )
        )

      case IActionType.AddToCart:
        return [
          <CustomHandle
            key={WorkflowNodeManager.getSourceHandle(id, 'insideCart')}
            id={WorkflowNodeManager.getSourceHandle(id, 'insideCart')}
            nodeId={id}
            branch='insideCart'
            style={defaultCustomHandleStyle}
            edges={edges}
            onPlusClick={handlePlusClick}
            onConnectionClick={handleConnectionClick}
            title='Add new node (Inside cart)'
            dotColor='blue-500'
            branchLabel='In Cart'
          />,
          <CustomHandle
            key={WorkflowNodeManager.getSourceHandle(id, 'outsideCart')}
            id={WorkflowNodeManager.getSourceHandle(id, 'outsideCart')}
            nodeId={id}
            branch='outsideCart'
            style={defaultCustomHandleStyle}
            edges={edges}
            onPlusClick={handlePlusClick}
            onConnectionClick={handleConnectionClick}
            title='Add new node (Outside cart)'
            dotColor='teal-500'
            branchLabel='New to cart'
          />,
        ]

      default:
        return [
          <CustomHandle
            key={`${id}-next-action`}
            id={`${id}-next-action`}
            nodeId={id}
            style={{ top: 0 }}
            edges={edges}
            onPlusClick={handlePlusClick}
            onConnectionClick={handleConnectionClick}
            title='Add new node'
          />,
        ]
    }
  }

  const nextActions = getNextActions()
  return (
    <div className='relative group'>
      {/* Node container */}
      <div className='relative'>
        {/* Left handle (connection dot) for non-trigger nodes */}
        {!isTrigger && (
          <Handle
            type='target'
            position={Position.Left}
            onClick={handleConnectionClick}
            className={NODE_STYLES.handle.target}
          />
        )}

        {/* Main node using CanvasNodeSquare */}
        <CanvasNodeSquare
          shapeType={nodeData.actionValue.id === SPECIAL_NODES.trigger ? 'start' : 'middle'}
          actionName={nodeData.actionValue.type.toString()}
          icon={
            isTrigger ? (
              <Play size={20} style={{ color: '#3b82f6' }} />
            ) : (
              customPluginActions?.find((action) => action.type === nodeData.actionValue.type)?.icon
            )
          }
          borderColor={invalidOutput ? 'error' : 'normal'}
          metadataIcon={
            invalidOutput ? (
              <div title='References deleted or invalid node output'>
                <MaterialIcon icon='warning' size={16} className='text-yellow-500' />
              </div>
            ) : undefined
          }
          onDelete={nodeData.actionValue.id === SPECIAL_NODES.trigger ? undefined : handleDeleteNode}
          onEdit={canEdit ? handleEditClick : undefined}
          nextActions={nextActions}
          onDoubleClick={canEdit ? handleEditClick : () => {}}
          onAdd={handlePlusClick}
          label={WorkflowNodeManager.getNodeLabel(nodeData, customPluginActions)}
          onRename={isTrigger ? undefined : handleRename}
          description={String(ActionDescription({ actionValue: nodeData.actionValue }) || actionDescription)}
        />
      </div>
    </div>
  )
}
